import { useMemo } from 'react';
import { useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';
import { Trigger } from '@/hooks/useTriggerStream';
import { buildDeviceColorMap } from './vizPalette';

interface Props {
  triggers: Trigger[];
  dark: boolean;
  bare?: boolean;
  /** Continuously replay the "filling up" sweep as a loop. */
  loop?: boolean;
  /** Hide the caption below the chart. */
  hideCaption?: boolean;
}

const LOOP_SECONDS = 14;


const BIN_MINUTES = 10;
const BINS = (24 * 60) / BIN_MINUTES;

/**
 * Accumulative time-of-day clock. Every trigger is folded onto a 24h circle:
 * repeated activity at the same time of day grows the mark on that spot day
 * after day. Each device sits on its own concentric ring — rings are centred
 * and spaced with an equally increasing diameter.
 */
export function CumulativeConstellationClock({ triggers, dark, bare = false, loop = false, hideCaption = false }: Props) {
  const t = useT(visualizeContent).charts;
  const W = 1200;
  const H = 900;
  const CX = W / 2;
  const CY = H / 2;
  const R_MIN = 70;
  const R_MAX = 400;

  const { rings, maxCount, days, total, totalMarks } = useMemo(() => {
    const perDevice = new Map<string, number[]>();
    const dayKeys = new Set<string>();
    triggers.forEach((t) => {
      const d = new Date(t.created_at);
      if (Number.isNaN(d.getTime()) || !t.device_id) return;
      let arr = perDevice.get(t.device_id);
      if (!arr) { arr = new Array(BINS).fill(0); perDevice.set(t.device_id, arr); }
      const minutes = d.getHours() * 60 + d.getMinutes();
      arr[Math.floor(minutes / BIN_MINUTES) % BINS]++;
      dayKeys.add(d.toDateString());
    });

    const ids = Array.from(perDevice.keys()).sort();
    const colors = buildDeviceColorMap(ids);
    const step = ids.length > 1 ? (R_MAX - R_MIN) / (ids.length - 1) : 0;
    const maxCount = Math.max(1, ...ids.flatMap((id) => perDevice.get(id)!));
    const maxMark = Math.max(4, Math.min(22, (step || 40) * 0.45));

    let order = 0;
    const rings = ids.map((id, idx) => {
      const radius = R_MIN + idx * step;
      const counts = perDevice.get(id)!;
      const marks = counts
        .map((count, i) => {
          if (count <= 0) return null;
          const angle = (i / BINS) * Math.PI * 2 - Math.PI / 2;
          const r = 1.5 + Math.sqrt(count / maxCount) * maxMark;
          return {
            i,
            count,
            r,
            order: order++,
            x: CX + Math.cos(angle) * radius,
            y: CY + Math.sin(angle) * radius,
          };
        })
        .filter(Boolean) as { i: number; count: number; r: number; order: number; x: number; y: number }[];
      return { id, radius, marks, color: colors.get(id) || 'var(--viz-accent)' };
    });

    return { rings, maxCount, days: dayKeys.size, total: triggers.length, totalMarks: Math.max(1, order) };
  }, [triggers]);


  const stroke = 'rgb(var(--site-fg) / 0.10)';
  const text = 'rgb(var(--site-fg) / 0.45)';

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: '85vh' }}>
        {/* Hour spokes + labels */}
        {Array.from({ length: 24 }, (_, i) => {
          const angle = (i / 24) * Math.PI * 2 - Math.PI / 2;
          const major = i % 3 === 0;
          return (
            <g key={i}>
              <line
                x1={CX + Math.cos(angle) * (R_MIN - 18)}
                y1={CY + Math.sin(angle) * (R_MIN - 18)}
                x2={CX + Math.cos(angle) * (R_MAX + 14)}
                y2={CY + Math.sin(angle) * (R_MAX + 14)}
                stroke={major ? 'rgb(var(--site-fg) / 0.12)' : 'rgb(var(--site-fg) / 0.05)'}
                strokeWidth={major ? 0.9 : 0.5}
              />
              {major && !bare && (
                <text
                  x={CX + Math.cos(angle) * (R_MAX + 30)}
                  y={CY + Math.sin(angle) * (R_MAX + 30)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={11}
                  fill={text}
                  fontFamily="ui-monospace, monospace"
                >
                  {String(i).padStart(2, '0')}
                </text>
              )}
            </g>
          );
        })}

        {/* One concentric ring per device */}
        {rings.map((ring) => (
          <g key={ring.id}>
            <circle cx={CX} cy={CY} r={ring.radius} fill="none" stroke={stroke} strokeWidth={0.7} />
            {ring.marks.map((m) => {
              const op = 0.3 + 0.55 * (m.count / maxCount);
              // Staggered fade-in: each mark gets its own slot in the fill cycle.
              const f = totalMarks > 1 ? Math.min(0.88, (m.order / totalMarks) * 0.88) : 0;
              const inEnd = Math.min(0.92, f + 0.06);
              return (
                <circle
                  key={m.i}
                  cx={m.x}
                  cy={m.y}
                  r={m.r}
                  fill={ring.color}
                  opacity={loop ? 0 : op}
                >
                  {loop && (
                    <animate
                      attributeName="opacity"
                      values={`0;0;${op};${op};0`}
                      keyTimes={`0;${f.toFixed(4)};${inEnd.toFixed(4)};0.96;1`}
                      dur={`${LOOP_SECONDS}s`}
                      repeatCount="indefinite"
                    />
                  )}
                </circle>
              );
            })}
          </g>
        ))}




        <text
          x={CX}
          y={CY - 16}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={15}
          fill={text}
          fontFamily="ui-monospace, monospace"
          fontWeight={600}
        >
          {t.clockTriggers(total)}
        </text>
        <text
          x={CX}
          y={CY + 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={13}
          fill={text}
          fontFamily="ui-monospace, monospace"
        >
          {t.clockFolded(days)}
        </text>
      </svg>
      {!bare && !hideCaption && (
        <div className="mt-3 text-xs font-mono tracking-wide text-sitefg/40">
          {t.clockCaption()}
        </div>
      )}
    </div>
  );
}
